/**
 * Task Service - API methods for the Task Engine
 *
 * Covers task CRUD, state-machine action endpoints, and clinical hold management.
 * All routes are prefixed with /api/v1 and require JWT authentication.
 */

import apiClient from './apiClient';
import type {
  Task,
  TaskWatcher,
  TaskListResponse,
  TaskSummary,
  WorkloadResponse,
  ListTasksParams,
  CreateTaskDto,
  UpdateTaskDto,
  CompleteTaskDto,
  SkipTaskDto,
  RescheduleTaskDto,
  ReassignTaskDto,
  AcknowledgeTaskDto,
  HoldTaskDto,
  HoldActionDto,
} from '../../types/tasks';

// ============================================================================
// Task CRUD
// ============================================================================

/**
 * GET /api/v1/tasks
 * List tasks for the organization with optional filters and pagination.
 */
export const getTasks = async (params?: ListTasksParams): Promise<TaskListResponse> => {
  // Wire shape is `{ success, data, pagination, message }` (see task.controller.ts /
  // task.service.ts findAll) — map `pagination` to the `meta` field our TaskListResponse
  // type exposes, rather than reading a `meta` key the API never sends.
  const response = await apiClient.get<{
    success: boolean;
    data: Task[];
    pagination: TaskListResponse['meta'];
  }>('/api/v1/tasks', { params });
  return {
    data: response.data.data,
    meta: response.data.pagination,
  };
};

/**
 * GET /api/v1/tasks/summary
 * Aggregate task counts by status and escalation tier for the organization.
 */
export const getTaskSummary = async (): Promise<TaskSummary> => {
  const response = await apiClient.get<{ success: boolean; data: TaskSummary }>(
    '/api/v1/tasks/summary',
  );
  return response.data.data;
};

/**
 * GET /api/v1/tasks/workload
 * Workload view across all assignees in the organization (supervisor/admin).
 * Accepts optional fromDate and toDate query params (ISO 8601 date strings).
 */
export const getWorkload = async (params?: {
  fromDate?: string;
  toDate?: string;
}): Promise<WorkloadResponse> => {
  const response = await apiClient.get<{ success: boolean; data: WorkloadResponse['data'] }>(
    '/api/v1/tasks/workload',
    { params },
  );
  return { data: response.data.data };
};

/**
 * GET /api/v1/tasks/:taskId
 * Get a single task by ID, scoped to the organization.
 */
export const getTask = async (taskId: string): Promise<Task> => {
  const response = await apiClient.get<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}`,
  );
  return response.data.data;
};

/**
 * POST /api/v1/tasks
 * Create a new ad-hoc task assigned to the specified user.
 */
export const createTask = async (dto: CreateTaskDto): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>('/api/v1/tasks', dto);
  return response.data.data;
};

/**
 * PATCH /api/v1/tasks/:taskId
 * Update a task's name and/or description.
 * Status transitions and assignment changes use the dedicated action endpoints.
 */
export const updateTask = async (taskId: string, dto: UpdateTaskDto): Promise<Task> => {
  const response = await apiClient.patch<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}`,
    dto,
  );
  return response.data.data;
};

// ============================================================================
// Task action endpoints (state-machine transitions)
// ============================================================================

/**
 * POST /api/v1/tasks/:taskId/complete
 * Transition the task to COMPLETED status.
 */
export const completeTask = async (taskId: string, dto?: CompleteTaskDto): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}/complete`,
    dto ?? {},
  );
  return response.data.data;
};

/**
 * POST /api/v1/tasks/:taskId/skip
 * Transition the task to SKIPPED status. A reason is required.
 * Set cascadeSkip=true to also skip active downstream dependent tasks.
 */
export const skipTask = async (taskId: string, dto: SkipTaskDto): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}/skip`,
    dto,
  );
  return response.data.data;
};

/**
 * POST /api/v1/tasks/:taskId/reschedule
 * Update the task's scheduled date. A reason is required.
 */
export const rescheduleTask = async (taskId: string, dto: RescheduleTaskDto): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}/reschedule`,
    dto,
  );
  return response.data.data;
};

/**
 * POST /api/v1/tasks/:taskId/reassign
 * Reassign the task to a different active organization member. A reason is required.
 */
export const reassignTask = async (taskId: string, dto: ReassignTaskDto): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}/reassign`,
    dto,
  );
  return response.data.data;
};

/**
 * POST /api/v1/tasks/:taskId/pin
 * Toggle the pinned flag on a task.
 */
export const togglePin = async (taskId: string): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}/pin`,
  );
  return response.data.data;
};

/**
 * POST /api/v1/tasks/:taskId/acknowledge
 * Acknowledge an escalation on a task, recording the option, notes, and commitment date.
 */
export const acknowledgeTask = async (
  taskId: string,
  dto: AcknowledgeTaskDto,
): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}/acknowledge`,
    dto,
  );
  return response.data.data;
};

/**
 * POST /api/v1/tasks/:taskId/hold
 * Submit a clinical hold request on a task. Requires workflow linkage.
 */
export const requestHold = async (taskId: string, dto: HoldTaskDto): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}/hold`,
    dto,
  );
  return response.data.data;
};

// ============================================================================
// Clinical hold management (task:manage_hold permission required)
// ============================================================================

/**
 * POST /api/v1/tasks/:taskId/hold/approve
 * Approve a pending hold request, placing the task into CLINICAL_HOLD status.
 */
export const approveHold = async (taskId: string): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}/hold/approve`,
  );
  return response.data.data;
};

/**
 * POST /api/v1/tasks/:taskId/hold/deny
 * Deny a pending hold request. An optional reason can accompany the denial.
 */
export const denyHold = async (taskId: string, dto?: HoldActionDto): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}/hold/deny`,
    dto ?? {},
  );
  return response.data.data;
};

/**
 * POST /api/v1/tasks/:taskId/hold/release
 * Manually release an active clinical hold before its auto-release time.
 */
export const releaseHold = async (taskId: string, dto?: HoldActionDto): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}/hold/release`,
    dto ?? {},
  );
  return response.data.data;
};

// ============================================================================
// Watcher endpoints
// ============================================================================

/**
 * GET /api/v1/tasks/:taskId/watchers
 * List all watchers for a task.
 */
export const getWatchers = async (taskId: string): Promise<TaskWatcher[]> => {
  const response = await apiClient.get<{ success: boolean; data: TaskWatcher[] }>(
    `/api/v1/tasks/${taskId}/watchers`,
  );
  return response.data.data;
};

/**
 * POST /api/v1/tasks/:taskId/watchers
 * Add a watcher to a task. Caller must be the task creator or assignee.
 */
export const addWatcher = async (taskId: string, userId: string): Promise<TaskWatcher> => {
  const response = await apiClient.post<{ success: boolean; data: TaskWatcher }>(
    `/api/v1/tasks/${taskId}/watchers`,
    { userId },
  );
  return response.data.data;
};

/**
 * DELETE /api/v1/tasks/:taskId/watchers/:userId
 * Remove a watcher from a task. Caller must be the task creator or assignee.
 */
export const removeWatcher = async (taskId: string, userId: string): Promise<void> => {
  await apiClient.delete(`/api/v1/tasks/${taskId}/watchers/${userId}`);
};

/**
 * POST /api/v1/tasks/:taskId/reopen
 * Reopen a completed/skipped task (Red Dot detail flows).
 */
export const reopenTask = async (taskId: string): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>(
    `/api/v1/tasks/${taskId}/reopen`,
    {},
  );
  return response.data.data;
};

/**
 * Update a task's status via the appropriate dedicated action endpoint.
 * - IN_PROGRESS  → POST /api/v1/tasks/:taskId/start
 * - BLOCKED      → POST /api/v1/tasks/:taskId/block  (reason required)
 * - AVAILABLE    → POST /api/v1/tasks/:taskId/unblock (for un-blocking)
 *
 * Note: COMPLETED → use completeTask(), SKIPPED → use skipTask(),
 *       reopening → use reopenTask().
 */
export const updateTaskStatus = async (
  taskId: string,
  status: string,
  statusReason?: string,
): Promise<Task> => {
  let endpoint: string;
  let payload: Record<string, unknown> | undefined;

  switch (status) {
    case 'IN_PROGRESS':
      endpoint = `/api/v1/tasks/${taskId}/start`;
      break;
    case 'BLOCKED':
      endpoint = `/api/v1/tasks/${taskId}/block`;
      payload = { reason: statusReason ?? 'Blocked' };
      break;
    case 'AVAILABLE':
      endpoint = `/api/v1/tasks/${taskId}/unblock`;
      break;
    default:
      // Fallback: attempt the start endpoint for any unrecognised forward-transition
      endpoint = `/api/v1/tasks/${taskId}/start`;
  }

  const response = await apiClient.post<{ success: boolean; data: Task }>(endpoint, payload ?? {});
  return response.data.data;
};

/**
 * POST /api/v1/tasks
 * Create a manual task within a workflow instance (Red Dot).
 */
export const createManualTask = async (data: {
  workflowInstanceId: string;
  name: string;
  type: string;
  assignedToUserId: string;
  description?: string;
  workflowStepKey?: string;
}): Promise<Task> => {
  const response = await apiClient.post<{ success: boolean; data: Task }>('/api/v1/tasks', {
    ...data,
    // The API requires workflowStepKey whenever workflowInstanceId is set. Red Dot
    // tasks have no step-key concept, so tag manually-added ones as 'MANUAL'. Without
    // this the create 400s and the task is silently never created (CDSS-…).
    workflowStepKey: data.workflowStepKey ?? 'MANUAL',
    scheduledDate: new Date().toISOString().split('T')[0],
  });
  return response.data.data;
};

// ============================================================================
// Default export (named object, matching workflowService convention)
// ============================================================================

const taskService = {
  getTasks,
  getTask,
  getTaskSummary,
  getWorkload,
  createTask,
  updateTask,
  completeTask,
  skipTask,
  rescheduleTask,
  reassignTask,
  togglePin,
  acknowledgeTask,
  requestHold,
  approveHold,
  denyHold,
  releaseHold,
  getWatchers,
  addWatcher,
  removeWatcher,
  reopenTask,
  updateTaskStatus,
  createManualTask,
};

export default taskService;
